import { useRef } from 'react';

import { type OperationContext } from 'urql';

/**
 * Returns a query context whose identity changes only when the last order is due a refresh.
 * urql re-runs a query when its context changes, so this decides every refetch: becoming
 * visible after the refresh window, a newly placed order, or a poll tick.
 */
export const useLastOrderStatusRefreshContext = (params: Params) => {
  //
  // ─── Refs ──────────────────────────────────────────────────────────────────

  const refreshStateRef = useRef<RefreshState | undefined>(undefined);

  // ─── Derived ───────────────────────────────────────────────────────────────

  refreshStateRef.current = getNextRefreshState({
    ...params,
    refreshState: refreshStateRef.current,
  });

  return refreshStateRef.current.context;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getNextRefreshState(params: GetNextRefreshStateParams): RefreshState {
  const {
    refreshState,
    refreshInterval,
    isVisible,
    isLoggedIn,
    placedOrderId,
    pollCount,
    hasLastFetchFailed,
  } = params;

  if (!refreshState) {
    return createRefreshState(params);
  }

  // The order param drops off whenever Home is reached another way, so keep the last one seen.
  const lastPlacedOrderId = placedOrderId ?? refreshState.placedOrderId;
  const nextStateParams = { ...params, placedOrderId: lastPlacedOrderId };

  // Login un-pauses the query, which fetches like a fresh mount, so only the window restarts.
  const hasLoggedIn = isLoggedIn && !refreshState.isLoggedIn;

  if (hasLoggedIn) {
    return { ...createRefreshState(nextStateParams), context: refreshState.context };
  }

  const hasBecomeVisible = isVisible && !refreshState.isVisible;
  const hasRefreshWindowElapsed = Date.now() - refreshState.refreshedAt >= refreshInterval;
  const isLastFetchStale = hasLastFetchFailed || hasRefreshWindowElapsed;
  const shouldRefreshOnVisible = hasBecomeVisible && isLastFetchStale;
  const hasPlacedNewOrder = lastPlacedOrderId !== refreshState.placedOrderId;
  const hasPolled = pollCount !== refreshState.pollCount;
  const isRefreshDue = shouldRefreshOnVisible || hasPlacedNewOrder || hasPolled;

  // A placed order or poll tick stays pending until Home is visible again.
  if (!isVisible || !isRefreshDue) {
    return { ...refreshState, isVisible, isLoggedIn };
  }

  return createRefreshState(nextStateParams);
}

function createRefreshState(params: Params): RefreshState {
  const { isVisible, isLoggedIn, placedOrderId, pollCount } = params;

  return {
    isVisible,
    isLoggedIn,
    placedOrderId,
    pollCount,
    refreshedAt: Date.now(),
    context: {},
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  refreshInterval: number;
  isVisible: boolean;
  isLoggedIn: boolean;
  placedOrderId: string | undefined;
  pollCount: number;
  hasLastFetchFailed: boolean;
};

type GetNextRefreshStateParams = Params & {
  refreshState: RefreshState | undefined;
};

type RefreshState = {
  isVisible: boolean;
  isLoggedIn: boolean;
  placedOrderId: string | undefined;
  pollCount: number;
  refreshedAt: number;
  context: Partial<OperationContext>;
};
